import { ApiError, getJSON } from '../../lib/api';

/** Mirrors the public contract of GET /health/ready. */
export interface DependencyCheck {
  name: string;
  status: 'up' | 'down';
  latency_ms: number;
  /** Optional dependencies degrade features without making the API unready. */
  optional?: boolean;
}

interface ReadyResponse {
  status: 'ready' | 'not_ready';
  checks: DependencyCheck[];
}

export type SystemState = 'checking' | 'operational' | 'degraded' | 'unreachable';

export interface HealthSnapshot {
  state: SystemState;
  checks: DependencyCheck[];
  checkedAt: Date | null;
}

function isReadyResponse(v: unknown): v is ReadyResponse {
  return typeof v === 'object' && v !== null && 'checks' in v && Array.isArray(v.checks);
}

/**
 * Fetches readiness. A 503 with a valid body means "degraded" (the API is up
 * but a dependency is not); any other failure means the API is unreachable.
 */
export async function fetchHealth(signal?: AbortSignal): Promise<HealthSnapshot> {
  const opts = signal ? { signal, timeoutMs: 5000 } : { timeoutMs: 5000 };
  try {
    const body = await getJSON<ReadyResponse>('/health/ready', opts);
    const limited = body.checks.some((c) => c.status !== 'up');
    return {
      state: limited ? 'degraded' : 'operational',
      checks: body.checks,
      checkedAt: new Date(),
    };
  } catch (err) {
    if (signal?.aborted) throw err;
    if (err instanceof ApiError && err.status === 503 && isReadyResponse(err.body)) {
      return { state: 'degraded', checks: err.body.checks, checkedAt: new Date() };
    }
    return { state: 'unreachable', checks: [], checkedAt: new Date() };
  }
}

export const dependencyLabels: Record<string, string> = {
  api: 'API Pro Guard',
  postgres: 'PostgreSQL',
  redis: 'Redis',
  nats: 'NATS',
  media: 'ZLMediaKit',
};

/** What stops working while an optional dependency is down. */
export const dependencyImpact: Record<string, string> = {
  media: 'Vídeo ao vivo indisponível; o restante da plataforma segue funcionando.',
};
